import { caseTemplateSchema } from "@/components/Forms/schema/caseTemplateSchema.ts";
import { taskTemplateSchema } from "@/components/Forms/schema/taskTemplateSchema.ts";
import useAuthenticatedFetch from "@/hooks/useAuthenticatedFetch.ts";
import { apiUrl } from "@/utils/api.ts";
import { type UseQueryResult, useQuery } from "@tanstack/react-query";
import { createDataSelector, createResourceSelector } from "jsonapi-zod-query";

const resourceSelector = createResourceSelector({
    type: "caseTemplate",
    attributesSchema: caseTemplateSchema,
    relationships: {
        taskTemplates: {
            relationshipType: "many",
            include: {
                type: "taskTemplate",
                attributesSchema: taskTemplateSchema,
            },
            required: false,
        },
    },
});

const caseTemplateTypeSelector = createDataSelector(resourceSelector);
export type CaseTemplates = ReturnType<typeof caseTemplateTypeSelector>;

type Props = {
    caseTemplateUUID: string | null;
    isQueryEnabled?: boolean;
};

export const useFetchCaseTemplate = ({
    isQueryEnabled = true,
    caseTemplateUUID,
}: Props): UseQueryResult<CaseTemplates> => {
    const fetch = useAuthenticatedFetch();

    return useQuery({
        refetchOnWindowFocus: false,
        queryKey: ["case-templates", caseTemplateUUID],
        enabled: isQueryEnabled,
        queryFn: async () => {
            const fetchURL = apiUrl(`/case-template/by-id/${caseTemplateUUID}`);

            const response = await fetch(fetchURL.href, {
                method: "GET",
                headers: {
                    "Content-Type": "application/vnd.api+json",
                },
            });

            return await response.json();
        },
        select: caseTemplateTypeSelector,
    });
};
